import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  ASK_USER_SKIP_SENTINEL,
  ASK_USER_TOOL_NAME,
  ConfirmationMessageTypeEnum,
  DataMessageTypeEnum,
  StreamConfirmationPartTypeName,
  TerraUIMessageDataPartName,
  type AskUserMessage,
  type BaseDataMessage,
  type ChatMessageBranchMetadata,
  type MentionEntity,
  type MentionsMessage,
  type MessageSegment,
  type PromptOption,
  type TerraUIMessageDataPart,
  type ToolCallGenericMessage,
  type UserConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import {
  readAskUserPrompt,
  readAskUserOptions,
} from '../components/tools/ask-user/ask-user-answer-card-view.utils';
import type { MentionEntityWithOffset } from '../editor/lexical/plugins/buildMessageSegments';
import {
  getUserMessageDisplayText,
  stripRichCardMetadataFromMessageText,
} from './userMessageDisplayText';

export type ChatComposerBranchEditSource = {
  kind: 'composer';
  messageId: string;
  message: TerraUIMessage;
  composerText: string;
  mentions: MentionEntity[];
  attachmentPartIndexes: number[];
};

export type ChatAskUserBranchEditSource = {
  kind: 'ask-user';
  messageId: string;
  message: TerraUIMessage;
  askUserDataMessage: AskUserMessage;
};

export type ChatBranchEditSource =
  | ChatComposerBranchEditSource
  | ChatAskUserBranchEditSource;

type DataOrConfirmationPart = Extract<
  TerraUIMessage['parts'][number],
  {
    type:
      | typeof TerraUIMessageDataPartName
      | typeof StreamConfirmationPartTypeName;
  }
> & { data: BaseDataMessage };

function isDataOrConfirmationPart(
  part: TerraUIMessage['parts'][number],
): part is DataOrConfirmationPart {
  return (
    part.type === TerraUIMessageDataPartName ||
    part.type === StreamConfirmationPartTypeName
  );
}

function isAskUserData(data: BaseDataMessage): data is AskUserMessage {
  return data.type === ConfirmationMessageTypeEnum.AskUser;
}

function isToolCallData(data: BaseDataMessage): data is ToolCallGenericMessage {
  return (
    data.type === DataMessageTypeEnum.ToolCallStart ||
    data.type === DataMessageTypeEnum.ToolCallUpdate ||
    data.type === DataMessageTypeEnum.ToolCallComplete ||
    data.type === DataMessageTypeEnum.ToolCallError
  );
}

function isAskUserToolCallData(
  data: BaseDataMessage,
): data is ToolCallGenericMessage {
  return isToolCallData(data) && data.toolName === ASK_USER_TOOL_NAME;
}

function isMentionsDataPart(
  part: TerraUIMessage['parts'][number],
): part is TerraUIMessageDataPart & { data: MentionsMessage } {
  return (
    part.type === TerraUIMessageDataPartName &&
    part.data.type === DataMessageTypeEnum.Mentions
  );
}

function getUserConfirmationFromMessage(
  message: TerraUIMessage,
): UserConfirmationMessage | null {
  const metadata = message.metadata;
  if (
    metadata &&
    typeof metadata === 'object' &&
    (metadata as { type?: unknown }).type ===
      ConfirmationMessageTypeEnum.UserConfirmation
  ) {
    return metadata as UserConfirmationMessage;
  }

  return null;
}

function buildAskUserMessageFromToolCall({
  toolCallPartId,
  toolCallId,
  input,
}: {
  toolCallPartId?: string;
  toolCallId?: string;
  input: unknown;
}): AskUserMessage | null {
  const prompt = readAskUserPrompt(input);
  const options = readAskUserOptions(input);
  if (!prompt || options.length === 0) {
    return null;
  }

  const anchorId = toolCallPartId ?? toolCallId;
  if (!anchorId) {
    return null;
  }

  return {
    type: ConfirmationMessageTypeEnum.AskUser,
    id: anchorId,
    askUserPrompt: {
      prompt,
      options,
    },
  };
}

function isNonEmptyString(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

type AskUserQuestionOccurrence = {
  messageIndex: number;
  partIds: string[];
  kind: 'data' | 'tool-call';
  askUserDataMessage: AskUserMessage;
};

function collectAskUserQuestionOccurrences(
  messages: TerraUIMessage[],
): AskUserQuestionOccurrence[] {
  return messages.flatMap((message, messageIndex) => {
    if (message.role !== 'assistant') {
      return [];
    }

    return (message.parts ?? []).flatMap<AskUserQuestionOccurrence>((part) => {
      if (!isDataOrConfirmationPart(part)) {
        return [];
      }

      const data = part.data;
      if (isAskUserData(data)) {
        return [
          {
            messageIndex,
            partIds: [part.id, data.id].filter(isNonEmptyString),
            kind: 'data',
            askUserDataMessage: data,
          },
        ];
      }

      if (isAskUserToolCallData(data)) {
        const askUserFromToolCall = buildAskUserMessageFromToolCall({
          toolCallPartId: part.id,
          toolCallId: data.toolCallId,
          input: data.input,
        });
        if (askUserFromToolCall) {
          return [
            {
              messageIndex,
              partIds: [part.id, data.toolCallId].filter(isNonEmptyString),
              kind: 'tool-call',
              askUserDataMessage: askUserFromToolCall,
            },
          ];
        }
      }

      return [];
    });
  });
}

type AskUserConfirmationEntry = {
  messageIndex: number;
  message: TerraUIMessage;
  confirmation: UserConfirmationMessage;
};

function collectAskUserConfirmations(
  messages: TerraUIMessage[],
): AskUserConfirmationEntry[] {
  return messages.flatMap((message, messageIndex) => {
    if (message.role !== 'user' || !message.id) {
      return [];
    }

    const confirmation = getUserConfirmationFromMessage(message);
    if (!confirmation || confirmation.toolName !== ASK_USER_TOOL_NAME) {
      return [];
    }

    return [{ messageIndex, message, confirmation }];
  });
}

export function getAskUserConfirmationAnchorIds({
  message,
}: {
  message: TerraUIMessage;
}): string[] {
  const confirmation = getUserConfirmationFromMessage(message);
  if (!confirmation || confirmation.toolName !== ASK_USER_TOOL_NAME) {
    return [];
  }

  return (confirmation.subs ?? []).filter((sub) => sub.length > 0);
}

function resolveSelectedOptionFromConfirmation({
  confirmation,
  askUserPrompt,
}: {
  confirmation: UserConfirmationMessage;
  askUserPrompt: AskUserMessage['askUserPrompt'];
}): PromptOption {
  const response = confirmation.response ?? '';
  if (response === ASK_USER_SKIP_SENTINEL) {
    return {
      value: ASK_USER_SKIP_SENTINEL,
      label: ASK_USER_SKIP_SENTINEL,
    };
  }

  const matchedOption = askUserPrompt.options.find(
    (option) => option.value === response || option.label === response,
  );
  if (matchedOption) {
    return matchedOption;
  }

  return {
    value: response,
    label: response,
  };
}

export function resolveAskUserBranchContext({
  messages,
  toolCallId,
  toolCallPartId,
}: {
  messages: TerraUIMessage[];
  toolCallId?: string;
  toolCallPartId?: string;
}): {
  userMessageId: string;
  userMessage: TerraUIMessage;
  askUserDataMessage: AskUserMessage;
  selectedOption: PromptOption;
} | null {
  const anchorIds = [toolCallPartId, toolCallId].filter(isNonEmptyString);

  if (anchorIds.length === 0) {
    return null;
  }

  const anchorIdSet = new Set(anchorIds);
  const occurrences = collectAskUserQuestionOccurrences(messages);
  const confirmations = collectAskUserConfirmations(messages);

  let clickedOccurrences = occurrences.filter((occurrence) =>
    occurrence.partIds.some((partId) => anchorIdSet.has(partId)),
  );

  if (clickedOccurrences.length === 0) {
    const anchoredConfirmationSubs = new Set(
      confirmations
        .filter(({ confirmation }) =>
          (confirmation.subs ?? []).some((sub) => anchorIdSet.has(sub)),
        )
        .flatMap(({ confirmation }) => confirmation.subs ?? []),
    );
    clickedOccurrences = occurrences.filter((occurrence) =>
      occurrence.partIds.some((partId) => anchoredConfirmationSubs.has(partId)),
    );
  }

  if (clickedOccurrences.length === 0) {
    return null;
  }

  const clickedOccurrence =
    clickedOccurrences.find((occurrence) => occurrence.kind === 'data') ??
    clickedOccurrences[0];

  const clickedIndexes = clickedOccurrences.map(
    (occurrence) => occurrence.messageIndex,
  );
  const firstClickedIndex = Math.min(...clickedIndexes);
  const lastClickedIndex = Math.max(...clickedIndexes);

  const distanceToClicked = (messageIndex: number): number => {
    if (messageIndex >= firstClickedIndex && messageIndex <= lastClickedIndex) {
      return 0;
    }
    return messageIndex > lastClickedIndex
      ? messageIndex - lastClickedIndex
      : firstClickedIndex - messageIndex;
  };

  const pickNearestToClicked = <Entry>(
    entries: Entry[],
    getMessageIndex: (entry: Entry) => number,
  ): Entry | undefined =>
    entries.reduce<Entry | undefined>((best, candidate) => {
      if (!best) {
        return candidate;
      }

      const bestIndex = getMessageIndex(best);
      const candidateIndex = getMessageIndex(candidate);
      const bestDistance = distanceToClicked(bestIndex);
      const candidateDistance = distanceToClicked(candidateIndex);
      if (candidateDistance !== bestDistance) {
        return candidateDistance < bestDistance ? candidate : best;
      }

      const bestIsAfter = bestIndex > lastClickedIndex;
      const candidateIsAfter = candidateIndex > lastClickedIndex;
      return candidateIsAfter && !bestIsAfter ? candidate : best;
    }, undefined);

  const clickedPartIds = new Set([
    ...anchorIds,
    ...clickedOccurrences.flatMap((occurrence) => occurrence.partIds),
  ]);
  const findConfirmationsForPartIds = (partIds: Set<string>) =>
    confirmations.filter(({ confirmation }) =>
      (confirmation.subs ?? []).some((sub) => partIds.has(sub)),
    );

  const directConfirmations = findConfirmationsForPartIds(clickedPartIds);

  const clickedPrompt =
    clickedOccurrence.askUserDataMessage.askUserPrompt.prompt;
  const promptLinkedCandidates =
    directConfirmations.length > 0
      ? []
      : occurrences
          .filter(
            (occurrence) =>
              !clickedOccurrences.includes(occurrence) &&
              occurrence.askUserDataMessage.askUserPrompt.prompt ===
                clickedPrompt,
          )
          .flatMap((occurrence) =>
            findConfirmationsForPartIds(new Set(occurrence.partIds)).map(
              (entry) => ({ occurrence, entry }),
            ),
          );

  const siblingSideCandidates = promptLinkedCandidates.filter(
    ({ occurrence }) =>
      clickedOccurrence.kind === 'tool-call'
        ? occurrence.messageIndex <= lastClickedIndex
        : occurrence.messageIndex >= firstClickedIndex,
  );

  const answeringConfirmation =
    pickNearestToClicked(directConfirmations, (entry) => entry.messageIndex) ??
    (
      pickNearestToClicked(
        siblingSideCandidates,
        ({ occurrence }) => occurrence.messageIndex,
      ) ??
      pickNearestToClicked(
        promptLinkedCandidates,
        ({ occurrence }) => occurrence.messageIndex,
      )
    )?.entry ??
    pickNearestToClicked(confirmations, (entry) => entry.messageIndex);

  if (!answeringConfirmation) {
    return null;
  }

  return {
    userMessageId: answeringConfirmation.message.id,
    userMessage: answeringConfirmation.message,
    askUserDataMessage: clickedOccurrence.askUserDataMessage,
    selectedOption: resolveSelectedOptionFromConfirmation({
      confirmation: answeringConfirmation.confirmation,
      askUserPrompt: clickedOccurrence.askUserDataMessage.askUserPrompt,
    }),
  };
}

export function extractChatBranchEditSource({
  message,
}: {
  message: TerraUIMessage;
}): ChatComposerBranchEditSource | null {
  if (message.role !== 'user') {
    return null;
  }

  const confirmation = getUserConfirmationFromMessage(message);
  if (confirmation?.toolName === ASK_USER_TOOL_NAME) {
    return null;
  }

  const mentionsPart = message.parts.find(isMentionsDataPart);

  const mentions = mentionsPart?.data.mentions ?? [];

  const attachmentPartIndexes = message.parts.reduce<number[]>(
    (indexes, part, index) => {
      if (
        part.type === TerraUIMessageDataPartName &&
        part.data.type === DataMessageTypeEnum.ConversationAttachments
      ) {
        indexes.push(index);
      }
      return indexes;
    },
    [],
  );

  const composerText = getUserMessageDisplayText({ message });
  if (!composerText.trim() && attachmentPartIndexes.length === 0) {
    return null;
  }

  return {
    kind: 'composer',
    messageId: message.id,
    message,
    composerText,
    mentions,
    attachmentPartIndexes,
  };
}

export function buildBranchResendMessageParts({
  source,
  text,
  mentions,
  positionedMentions,
  buildSegments,
  newAttachmentParts,
}: {
  source: ChatComposerBranchEditSource;
  text: string;
  mentions: MentionEntity[];
  positionedMentions: MentionEntityWithOffset[];
  buildSegments: (
    textValue: string,
    positioned: MentionEntityWithOffset[],
  ) => MessageSegment[];
  newAttachmentParts: TerraUIMessage['parts'];
}): TerraUIMessage['parts'] {
  const messageParts: TerraUIMessage['parts'] = [];

  if (text) {
    messageParts.push({ type: 'text', text });
  }

  if (mentions.length > 0) {
    const displayText = stripRichCardMetadataFromMessageText(text);
    const segments = buildSegments(displayText, positionedMentions);
    messageParts.push({
      type: TerraUIMessageDataPartName,
      data: {
        type: DataMessageTypeEnum.Mentions,
        mentions,
        ...(segments.length > 0 ? { segments } : {}),
      },
    });
  }

  if (newAttachmentParts.length > 0) {
    messageParts.push(...newAttachmentParts);
    return messageParts;
  }

  for (const attachmentPartIndex of source.attachmentPartIndexes) {
    const attachmentPart = source.message.parts[attachmentPartIndex];
    if (attachmentPart) {
      messageParts.push(attachmentPart);
    }
  }

  return messageParts;
}

export function buildAskUserBranchResendMessage({
  selectedOption,
  askUserDataMessage,
}: {
  selectedOption: PromptOption;
  askUserDataMessage: AskUserMessage;
}): Pick<TerraUIMessage, 'parts'> & { metadata: UserConfirmationMessage } {
  const responseText = selectedOption.label || selectedOption.value;

  return {
    parts: responseText ? [{ type: 'text', text: responseText }] : [],
    metadata: {
      subs: askUserDataMessage.id ? [askUserDataMessage.id] : [],
      response: responseText,
      message: '',
      type: ConfirmationMessageTypeEnum.UserConfirmation,
      toolName: ASK_USER_TOOL_NAME,
    },
  };
}

export function createBranchMessageMetadata({
  branchFromMessageId,
}: {
  branchFromMessageId: string;
}): ChatMessageBranchMetadata {
  return { branchFromMessageId };
}
